import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Background,
  Controls,
  Edge,
  MarkerType,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useSearchParams } from 'react-router-dom';
import { Chip, TextField, Typography, makeStyles, useTheme } from '@material-ui/core';
import { Content, Page, Progress, ResponseErrorPanel } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  ArchGraph,
  ArchNode,
  EdgeCategory,
  NodeCategory,
  buildGraph,
  filterGraph,
} from './graph';
import { LAYER_GAP, NODE_HEIGHT, NODE_WIDTH, layoutGraph } from './layout';
import {
  CATEGORY_COLORS,
  CATEGORY_LABELS,
  EDGE_COLORS,
  EDGE_LABELS,
  EntityFlowNode,
  GroupFlowNode,
  Highlight,
  HighlightContext,
  GroupHealth,
  HealthContext,
  nodeTypes,
} from './nodes';
import { EntityPanel } from './EntityPanel';
import { STATE_COLORS, usePlatformHealth } from '../health/api';

const HEALTH_LEGEND = [
  { label: 'в порядке', color: STATE_COLORS.ok, mark: '' },
  { label: 'остановлен', color: STATE_COLORS.stopped, mark: '' },
  { label: 'внимание', color: STATE_COLORS.warning, mark: '▲' },
  { label: 'авария', color: STATE_COLORS.critical, mark: '✕' },
];

const ALL_CATEGORIES: NodeCategory[] = ['component', 'api', 'resource', 'external'];
const ALL_EDGES: EdgeCategory[] = ['dependsOn', 'providesApi', 'consumesApi'];

const ENTITY_FIELDS = [
  'kind',
  'metadata.name',
  'metadata.namespace',
  'metadata.title',
  'metadata.description',
  'metadata.links',
  'metadata.tags',
  'spec.type',
  'spec.lifecycle',
  'spec.owner',
  'spec.system',
  'relations',
];

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  },
  legendItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginRight: 8,
  },
  legendMark: {
    display: 'inline-block',
    minWidth: 8,
    height: 8,
    borderRadius: 8,
    color: '#fff',
    fontSize: 9,
    lineHeight: '8px',
    textAlign: 'center',
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
  },
  title: {
    fontWeight: 700,
    marginRight: theme.spacing(2),
  },
  group: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 6,
    marginRight: theme.spacing(2),
  },
  groupLabel: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginRight: 2,
  },
  search: {
    width: 240,
  },
  body: {
    flex: 1,
    display: 'flex',
    minHeight: 0,
  },
  canvas: {
    flex: 1,
    minWidth: 0,
    position: 'relative',
    // Связи только показывают зависимость и не должны перехватывать указатель у узлов:
    // иначе при наведении узел получает mouseleave от линии поверх него, и подсветка мерцает.
    '& .react-flow__edge, & .react-flow__edge-interaction': {
      pointerEvents: 'none',
    },
  },
  center: {
    padding: theme.spacing(4),
  },
}));

// Сторона крепления связи: внутри системы — слева направо (как раскладка ELK); между группами —
// сверху/снизу, если узлы разнесены в основном по вертикали (типично для полосы общих ресурсов),
// иначе слева/справа.
const pickHandles = (
  source: { x: number; y: number } | undefined,
  target: { x: number; y: number } | undefined,
  crossSystem: boolean,
) => {
  if (!crossSystem || !source || !target) return { sourceHandle: 's-r', targetHandle: 't-l' };
  const dx = target.x - source.x;
  const dy = target.y - source.y;
  if (Math.abs(dy) > Math.abs(dx) * 0.5) {
    return dy > 0
      ? { sourceHandle: 's-b', targetHandle: 't-t' }
      : { sourceHandle: 's-t', targetHandle: 't-b' };
  }
  return dx > 0
    ? { sourceHandle: 's-r', targetHandle: 't-l' }
    : { sourceHandle: 's-l', targetHandle: 't-r' };
};

// Вертикаль ступенчатой связи ставится в промежуток перед слоем цели, а не посередине между узлами:
// иначе у целей во второй колонке слоя она проходит сквозь первую.
const EDGE_OFFSET = 20; // отступ smoothstep от точки крепления по умолчанию
const stepPosition = (
  centers: Map<string, { x: number; y: number }>,
  lanes: Map<string, number>,
  sourceId: string,
  targetId: string,
) => {
  const source = centers.get(sourceId);
  const target = centers.get(targetId);
  const lane = lanes.get(targetId);
  if (!source || !target || lane === undefined) return undefined;
  const from = source.x + NODE_WIDTH / 2 + EDGE_OFFSET;
  const to = target.x - NODE_WIDTH / 2 - EDGE_OFFSET;
  if (to <= from) return undefined;
  return Math.min(1, Math.max(0, (lane - LAYER_GAP / 2 - from) / (to - from)));
};

const toggle = <T,>(set: Set<T>, value: T) => {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
};

const ArchitectureGraph = () => {
  const classes = useStyles();
  const theme = useTheme();
  const catalogApi = useApi(catalogApiRef);
  const reactFlow = useReactFlow();

  const [graph, setGraph] = useState<ArchGraph>();
  const [error, setError] = useState<Error>();
  const [categories, setCategories] = useState(new Set<NodeCategory>(ALL_CATEGORIES));
  const [edgeCategories, setEdgeCategories] = useState(new Set<EdgeCategory>(ALL_EDGES));
  const [groups, setGroups] = useState<Set<string>>();
  const [flowNodes, setFlowNodes] = useState<(GroupFlowNode | EntityFlowNode)[]>([]);
  // Номер раскладки: ReactFlow пересоздаётся на каждую новую раскладку, и проп fitView подгоняет
  // масштаб под неё при инициализации (вызов fitView из эффекта срабатывал до измерения узлов).
  const [layoutVersion, setLayoutVersion] = useState(0);
  const [centers, setCenters] = useState(new Map<string, { x: number; y: number }>());
  const [lanes, setLanes] = useState(new Map<string, number>());
  // Выбранный узел хранится в адресе (?entity=component:default/…), ссылкой на него можно поделиться.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('entity') ?? undefined;
  const setSelectedId = useCallback(
    (id?: string) =>
      setSearchParams(
        params => {
          const next = new URLSearchParams(params);
          if (id) next.set('entity', id);
          else next.delete('entity');
          return next;
        },
        { replace: true },
      ),
    [setSearchParams],
  );
  const [hoveredId, setHoveredId] = useState<string>();
  const [search, setSearch] = useState('');

  useEffect(() => {
    catalogApi
      .getEntities({
        filter: { kind: ['component', 'api', 'resource', 'system'] },
        fields: ENTITY_FIELDS,
      })
      .then(response => {
        const built = buildGraph(response.items);
        setGraph(built);
        setGroups(new Set(built.groups.map(g => g.id)));
      })
      .catch(setError);
  }, [catalogApi]);

  const visible = useMemo(
    () =>
      graph && groups
        ? filterGraph(graph, { categories, edgeCategories, groups })
        : undefined,
    [graph, categories, edgeCategories, groups],
  );

  // Раскладка пересчитывается только при смене фильтров, а не при наведении или выборе.
  useEffect(() => {
    if (!visible) return undefined;
    let cancelled = false;
    layoutGraph(visible)
      .then(layout => {
        if (cancelled) return;
        const groupNodes: GroupFlowNode[] = visible.groups.map(group => {
          const box = layout.groups.get(group.id)!;
          return {
            id: group.id,
            type: 'system',
            position: { x: box.x, y: box.y },
            style: { width: box.width, height: box.height },
            data: { label: group.label },
            draggable: false,
            selectable: false,
            zIndex: -1,
          };
        });
        const entityNodes: EntityFlowNode[] = visible.nodes.map(node => ({
          id: node.id,
          type: 'entity',
          parentId: node.groupId,
          position: layout.nodes.get(node.id) ?? { x: 0, y: 0 },
          data: {
            label: node.label,
            subtitle: node.subtitle,
            category: node.category,
          },
          draggable: false,
          zIndex: 1, // узлы всегда над слоем связей
        }));
        setFlowNodes([...groupNodes, ...entityNodes]);
        setCenters(layout.absolute);
        setLanes(layout.lanes);
        setLayoutVersion(v => v + 1);
      })
      .catch(setError);
    return () => {
      cancelled = true;
    };
  }, [visible]);

  const nodesById = useMemo(
    () => new Map((graph?.nodes ?? []).map(n => [n.id, n] as [string, ArchNode])),
    [graph],
  );

  // Подсветка: при наведении или выборе узла видны он, его соседи и их связи, остальное приглушено.
  const activeId = hoveredId ?? selectedId;
  const highlighted = useMemo(() => {
    if (!activeId || !visible) return undefined;
    const ids = new Set([activeId]);
    for (const edge of visible.edges) {
      if (edge.source === activeId) ids.add(edge.target);
      if (edge.target === activeId) ids.add(edge.source);
    }
    return ids;
  }, [activeId, visible]);

  const { value: platformHealth } = usePlatformHealth();
  const healthByRef = useMemo(
    () => new Map((platformHealth?.entities ?? []).map(e => [e.entityRef, e])),
    [platformHealth],
  );
  // Сводка по системам для заголовков групп: сколько сущностей в аварии и с предупреждениями.
  const healthContext = useMemo(() => {
    const byGroup = new Map<string, GroupHealth>();
    for (const node of graph?.nodes ?? []) {
      const status = healthByRef.get(node.id)?.status;
      if (status !== 'critical' && status !== 'warning') continue;
      const g = byGroup.get(node.groupId) ?? { critical: 0, warning: 0 };
      g[status]++;
      byGroup.set(node.groupId, g);
    }
    return { byEntity: healthByRef, byGroup };
  }, [graph, healthByRef]);

  const highlight = useMemo<Highlight>(() => {
    if (!highlighted) return { selectedId };
    const groupIds = new Set<string>();
    for (const id of highlighted) {
      const node = nodesById.get(id);
      if (node) groupIds.add(node.groupId);
    }
    return { nodes: highlighted, groups: groupIds, selectedId };
  }, [highlighted, nodesById, selectedId]);

  const edges: Edge[] = useMemo(
    () =>
      (visible?.edges ?? []).map(edge => {
        const color = EDGE_COLORS[edge.category];
        const touches = edge.source === activeId || edge.target === activeId;
        // Связи между системами рисуются кривыми и приглушены, пока не выбран или не наведён узел:
        // иначе они перекрывают раскладку внутри систем.
        const crossSystem =
          nodesById.get(edge.source)?.groupId !== nodesById.get(edge.target)?.groupId;
        let opacity = crossSystem ? 0.1 : 0.85;
        if (activeId) opacity = touches ? 0.95 : 0.05;
        const { sourceHandle, targetHandle } = pickHandles(
          centers.get(edge.source),
          centers.get(edge.target),
          crossSystem,
        );
        return {
          id: edge.id,
          source: edge.source,
          target: edge.target,
          sourceHandle,
          targetHandle,
          type: crossSystem ? 'default' : 'smoothstep',
          pathOptions: crossSystem
            ? undefined
            : { stepPosition: stepPosition(centers, lanes, edge.source, edge.target) },
          interactionWidth: 0,
          style: {
            stroke: color,
            strokeWidth: touches ? 2.2 : 1.2,
            strokeDasharray: edge.category === 'consumesApi' ? '5 4' : undefined,
            opacity,
          },
          markerEnd: { type: MarkerType.ArrowClosed, color, width: 16, height: 16 },
        };
      }),
    [visible, activeId, nodesById, centers, lanes],
  );

  // Выбор узла: панель открывается справа и сужает холст, поэтому центрируем узел уже после её
  // появления. Масштаб сохраняется, при слишком мелком — приближаем (поиск, переход из панели).
  const focus = useCallback(
    (id: string, zoomIn = true) => {
      setSelectedId(id);
      window.setTimeout(() => {
        const node = reactFlow.getInternalNode(id);
        if (!node) return;
        const { x, y } = node.internals.positionAbsolute;
        const zoom = reactFlow.getZoom();
        reactFlow.setCenter(x + NODE_WIDTH / 2, y + NODE_HEIGHT / 2, {
          zoom: zoomIn ? Math.max(zoom, 0.9) : zoom,
          duration: 400,
        });
      }, 80);
    },
    [reactFlow, setSelectedId],
  );

  // Узел из адреса выбирается после первой раскладки; общий fitView в этом случае не нужен.
  const pendingFocus = useRef(selectedId);
  useEffect(() => {
    const id = pendingFocus.current;
    if (!layoutVersion || !id) return;
    pendingFocus.current = undefined;
    focus(id);
  }, [layoutVersion, focus]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSelectedId(undefined);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setSelectedId]);

  const searchOptions = useMemo(
    () =>
      (visible?.nodes ?? [])
        .map(n => n.label)
        .sort((a, b) => a.localeCompare(b)),
    [visible],
  );

  const onSearch = (value: string) => {
    setSearch(value);
    const query = value.trim().toLocaleLowerCase();
    if (!query) return;
    const match =
      visible?.nodes.find(n => n.label.toLocaleLowerCase() === query) ??
      visible?.nodes.find(n => n.entity.metadata.name.toLocaleLowerCase() === query);
    if (match) focus(match.id);
  };

  if (error) {
    return (
      <div className={classes.center}>
        <ResponseErrorPanel error={error} />
      </div>
    );
  }
  if (!graph || !groups) return <Progress />;

  const selected = selectedId ? nodesById.get(selectedId) : undefined;

  return (
    <div className={classes.root}>
      <div className={classes.toolbar}>
        <Typography variant="h6" className={classes.title}>
          Архитектура
        </Typography>
        <div className={classes.group}>
          <span className={classes.groupLabel}>Показать:</span>
          {ALL_CATEGORIES.map(category => (
            <Chip
              key={category}
              size="small"
              label={CATEGORY_LABELS[category]}
              clickable
              onClick={() => setCategories(toggle(categories, category))}
              style={
                categories.has(category)
                  ? { background: CATEGORY_COLORS[category], color: '#fff' }
                  : { borderColor: CATEGORY_COLORS[category] }
              }
              variant={categories.has(category) ? 'default' : 'outlined'}
            />
          ))}
        </div>
        <div className={classes.group}>
          <span className={classes.groupLabel}>Связи:</span>
          {ALL_EDGES.map(category => (
            <Chip
              key={category}
              size="small"
              label={EDGE_LABELS[category]}
              clickable
              onClick={() => setEdgeCategories(toggle(edgeCategories, category))}
              style={
                edgeCategories.has(category)
                  ? { background: EDGE_COLORS[category], color: '#fff' }
                  : { borderColor: EDGE_COLORS[category] }
              }
              variant={edgeCategories.has(category) ? 'default' : 'outlined'}
            />
          ))}
        </div>
        <div className={classes.group}>
          <span className={classes.groupLabel}>Состояние:</span>
          {HEALTH_LEGEND.map(item => (
            <span key={item.label} className={classes.legendItem}>
              <span className={classes.legendMark} style={{ background: item.color }}>
                {item.mark}
              </span>
              {item.label}
            </span>
          ))}
        </div>
        <TextField
          className={classes.search}
          size="small"
          variant="outlined"
          placeholder="Найти сервис или ресурс"
          value={search}
          onChange={e => onSearch(e.target.value)}
          inputProps={{ list: 'architecture-search' }}
        />
        <datalist id="architecture-search">
          {searchOptions.map(option => (
            <option key={option} value={option} />
          ))}
        </datalist>
        <div className={classes.group} style={{ flexBasis: '100%' }}>
          <span className={classes.groupLabel}>Системы:</span>
          {graph.groups.map(group => (
            <Chip
              key={group.id}
              size="small"
              label={group.label}
              clickable
              color={groups.has(group.id) ? 'primary' : 'default'}
              variant={groups.has(group.id) ? 'default' : 'outlined'}
              onClick={() => setGroups(toggle(groups, group.id))}
            />
          ))}
        </div>
      </div>
      <div className={classes.body}>
        <div className={classes.canvas}>
          <HighlightContext.Provider value={highlight}>
            <HealthContext.Provider value={healthContext}>
            <ReactFlow
              key={layoutVersion}
              fitView={!pendingFocus.current}
              fitViewOptions={{ padding: 0.04 }}
              nodes={flowNodes}
              edges={edges}
              nodeTypes={nodeTypes}
              colorMode={theme.palette.type === 'dark' ? 'dark' : 'light'}
              nodesDraggable={false}
              nodesConnectable={false}
              edgesFocusable={false}
              minZoom={0.1}
              maxZoom={2}
              onlyRenderVisibleElements
              proOptions={{ hideAttribution: true }}
              elementsSelectable={false}
              onNodeClick={(_, node) => node.type === 'entity' && focus(node.id, false)}
              onNodeMouseEnter={(_, node) => node.type === 'entity' && setHoveredId(node.id)}
              onNodeMouseLeave={() => setHoveredId(undefined)}
              onPaneClick={() => setSelectedId(undefined)}
            >
              <Background gap={24} />
              <Controls showInteractive={false} />
            </ReactFlow>
            </HealthContext.Provider>
          </HighlightContext.Provider>
        </div>
        {selected && (
          <EntityPanel
            node={selected}
            health={healthByRef.get(selected.id)}
            nodesById={nodesById}
            edges={graph.edges}
            onSelect={focus}
            onClose={() => setSelectedId(undefined)}
          />
        )}
      </div>
    </div>
  );
};

export const ArchitecturePage = () => (
  <Page themeId="home">
    <Content noPadding>
      <ReactFlowProvider>
        <ArchitectureGraph />
      </ReactFlowProvider>
    </Content>
  </Page>
);
